import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../../firebase";
import {
  IconUser,
  IconChevronDown,
  IconSettings,
  IconLogout,
  IconEye,
} from "@tabler/icons-react";
import { cn } from "../../lib/utils";
import { useAuth } from "../../hooks/useAuth";
import { useUIVersion } from "../../contexts/UIVersionContext";

const LOGO_URL =
  "https://firebasestorage.googleapis.com/v0/b/wealthbuilders-crm-9c323.firebasestorage.app/o/fa509ca3-1165-43d5-b075-f174c232cb04.png?alt=media&token=0f5855f4-8176-47ca-b842-6d7d1301b939";

// View modes for admin preview
const VIEW_MODES = ["Admin", "Premium", "Paid", "Free"];

const ModernHeader = ({ onProfileClick, user, onUI20SettingsClick }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { previewRole, isPreviewMode } = useUIVersion();

  // Get user data
  let u = user;
  if (!u) {
    try {
      u = JSON.parse(localStorage.getItem("authUser") || "null");
    } catch {
      u = null;
    }
  }

  const firstName = (u?.name || u?.fullName || u?.displayName || "User").split(
    " "
  )[0];
  const initials = firstName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleAvatarClick = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const handleProfileClick = () => {
    setDropdownOpen(false);
    navigate("/profile");
  };

  const handleLogout = async () => {
    try {
      // Sign out from Firebase
      await signOut(auth);

      // Clear ONLY auth-related data from localStorage
      // Preserve tracker data and other app state
      localStorage.removeItem("authUser");
      localStorage.removeItem("wb.plan");
      localStorage.removeItem("wb.userId");
      localStorage.removeItem("wb.userEmail");
      localStorage.removeItem("wb.userName");

      // Close the dropdown menu
      setDropdownOpen(false);

      // Navigate to login page
      navigate("/login");
    } catch (error) {
      console.error("Error during logout:", error);
      // Even if Firebase signOut fails, clear local data and redirect
      localStorage.removeItem("authUser");
      localStorage.removeItem("wb.plan");
      localStorage.removeItem("wb.userId");
      localStorage.removeItem("wb.userEmail");
      localStorage.removeItem("wb.userName");
      navigate("/login");
    }
  };

  return (
    <header className="modern-header">
      {/* Left side - Logo */}
      <div className="modern-header-left">
        <div className="modern-brand">
          <img
            src={LOGO_URL}
            alt="Wealth Builders"
            className="modern-brand-logo"
            loading="eager"
            decoding="async"
            onError={(e) => {
              // Fallback to a simple text logo if image fails
              e.currentTarget.style.display = "none";
              const parent = e.currentTarget.parentElement;
              const textLogo = document.createElement("div");
              textLogo.textContent = "WealthBuilders";
              textLogo.className = "modern-brand-text";
              parent.appendChild(textLogo);
            }}
          />
        </div>
      </div>

      {/* Right side - User section */}
      <div className="modern-header-right">
        {/* Preview Mode Indicator */}
        {isPreviewMode && (
          <button
            className="modern-preview-indicator"
            onClick={() => onUI20SettingsClick?.()}
            title="Preview mode active. Click to change."
          >
            <IconEye size={16} />
            <span className="preview-label">Viewing as: {previewRole}</span>
          </button>
        )}
        
        {/* UI 2.0 Settings Button */}
        {localStorage.getItem("wb.plan") === "Admin" && onUI20SettingsClick && (
          <button
            className="modern-ui20-settings-btn"
            onClick={() => onUI20SettingsClick?.()}
            title="Open UI 2.0 Settings"
          >
            <IconSettings size={16} />
          </button>
        )}
        
        <span className="modern-welcome">Welcome, {firstName}</span>

        <div className="modern-user-section" ref={dropdownRef}>
          <button
            className={cn("modern-avatar-button", dropdownOpen && "active")}
            onClick={handleAvatarClick}
            title="User menu"
          >
            <div className="modern-avatar">{initials}</div>
            <IconChevronDown
              size={16}
              className={cn("modern-chevron", dropdownOpen && "rotated")}
            />
          </button>

          {/* Dropdown Menu */}
          {dropdownOpen && (
            <div className="modern-dropdown">
              <div className="modern-dropdown-header">
                <div className="modern-avatar large">{initials}</div>
                <div className="modern-user-info">
                  <div className="modern-user-name">{firstName}</div>
                  <div className="modern-user-email">
                    {u?.email || "user@example.com"}
                  </div>
                </div>
              </div>

              <div className="modern-dropdown-divider" />

              <div className="modern-dropdown-items">
                <button
                  className="modern-dropdown-item"
                  onClick={handleProfileClick}
                >
                  <IconUser size={16} />
                  Profile
                </button>
                <button className="modern-dropdown-item">
                  <IconSettings size={16} />
                  Settings
                </button>
                <div className="modern-dropdown-divider" />
                <button
                  className="modern-dropdown-item danger"
                  onClick={handleLogout}
                >
                  <IconLogout size={16} />
                  Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default ModernHeader;
